import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { MessageCircle, Send, Trash2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EngagementBar from '../components/EngagementBar';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function Anonymous() {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [shares, setShares] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadShares();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadShares = async () => {
    try {
      const { data } = await api.get('/anonymous-shares');
      setShares(data);
    } catch (error) {
      console.error('Failed to load shares:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/anonymous-shares', { content, category });
      toast.success('Your story has been shared anonymously');
      setContent('');
      setCategory('');
      loadShares();
    } catch (error) {
      toast.error('Failed to share');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <MessageCircle className="w-10 h-10" style={{ color: '#E57A44' }} />
            <h1 className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              Anonymous Sharing
            </h1>
          </div>
          <p className="text-lg" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            Share your struggles safely and support others on their journey
          </p>
        </div>

        <div className="p-8 rounded-3xl mb-12" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            What are you walking through in this season?
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600 }}>
                Category (Optional)
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="e.g., Marriage, Family, Personal"
                data-testid="anonymous-category"
              />
            </div>
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600 }}>
                Your Story
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                rows={6}
                className="w-full px-4 py-3 rounded-xl border"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="Share your struggle, experience, or journey..."
                data-testid="anonymous-content"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-full transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
              data-testid="anonymous-submit"
            >
              <Send className="w-5 h-5" />
              <span>{submitting ? 'Sharing...' : 'Share Anonymously'}</span>
            </button>
          </form>
        </div>

        <div>
          <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            Community Shares
          </h2>
          <div className="space-y-6">
            {shares.map((share) => (
              <div key={share.id} className="p-6 rounded-2xl relative" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }} data-testid={`share-card-${share.id}`}>
                {user?.role === 'admin' && (
                  <button
                    onClick={async () => {
                      if (!window.confirm('Delete this share permanently? This also removes any comments and reactions on it.')) return;
                      try {
                        await api.delete(`/admin/anonymous-shares/${share.id}`);
                        toast.success('Share removed');
                        setShares((prev) => prev.filter((s) => s.id !== share.id));
                      } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete'); }
                    }}
                    className="absolute top-3 right-3 p-2 rounded-full transition-all duration-300 hover:scale-110 opacity-70 hover:opacity-100"
                    style={{ backgroundColor: '#E57A4415', color: '#E57A44' }}
                    aria-label="Delete share"
                    title="Delete this share (admin only)"
                    data-testid={`admin-delete-share-${share.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {share.category && (
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3" style={{ backgroundColor: '#E8C26520', color: '#E8C265', fontFamily: 'Nunito, sans-serif' }}>
                    {share.category}
                  </span>
                )}
                <p style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: '1.8' }}>{share.content}</p>
                <p className="mt-3 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                  Shared {new Date(share.created_at).toLocaleDateString()}
                </p>
                <EngagementBar parentType="share" parentId={share.id} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
